import React, { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { goBack as goBackReactRouter, push } from 'react-router-redux';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { rescheduleFixedSession } from 'api/photo-session';
import { isProblemSessionRescheduleEnabled } from 'data/feature-flags/problemSessionReschedule';
import AppBar from 'components/AppBar';
import { LoadingPage } from 'components/LoadingPage';
import useSchedulesDates from 'data/react-query/queries/useSchedulesDates';
import globalMessages from 'messages/globalMessages';
import ROUTES from 'routes/constants';
import { logUncaught } from 'utils/log';
import { getUser } from 'utils/user';

import messages from './messages';
import { photoSessionRescheduleConfirmOnClickAnalytics, photoSessionRescheduleSuccessViewedAnalytics } from './actions';
import { SchedulesDatesContent } from './SchedulesDatesContent';
import {
  buildButtons,
  buildFixedPayload,
  isLastPhotographerUnavailableError,
  mapSchedulesDatesAndTime,
} from './utils';
import { RESCHEDULE_ERROR, SESSION_TYPE } from './constants';
import useRescheduleSessionDetail from './useRescheduleSessionDetail';
import { EmptySchedulesState, ErrorState } from './RescheduleFeedback';

export const SchedulesDates = ({ params }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { user_id: userId, userAgentId } = getUser();

  const [chosenDate, setChosenDate] = useState();
  const [chosenTime, setChosenTime] = useState();
  const [newSessionAgentId, setNewSessionAgentId] = useState();
  const [rescheduleError, setRescheduleError] = useState();
  const [isSubmited, setIsSubmited] = useState(false);
  // "Manter a sessão comigo": when on, the reschedule requires the last photographer (requireLastAgent).
  const [keepSession, setKeepSession] = useState(true);
  // Feature-flag rollout gate. When off, the keep-session option is hidden and requireLastAgent is false.
  const [keepSessionEnabled, setKeepSessionEnabled] = useState(false);
  // The form waits on this so confirm cannot run before the flag is known.
  const [isKeepSessionFlagLoading, setIsKeepSessionFlagLoading] = useState(true);

  useEffect(() => {
    let active = true;
    isProblemSessionRescheduleEnabled()
      .then((enabled) => {
        if (active) {
          setKeepSessionEnabled(enabled);
        }
      })
      .catch(() => {
        // Keep the gate closed on failure so confirm never runs with an unknown flag.
        if (active) {
          setKeepSessionEnabled(false);
        }
      })
      .finally(() => {
        if (active) {
          setIsKeepSessionFlagLoading(false);
        }
      });
    return () => { active = false; };
  }, []);

  // Reset the selection on toggle: the available dates/times differ a lot between a single photographer's
  // agenda and the whole region's, so a previous pick could silently point at the wrong slot.
  const onChangeKeepSession = useCallback(({ target: { checked } }) => {
    setKeepSession(checked);
    setChosenDate();
    setChosenTime();
  }, []);

  const goToBasePath = useCallback(() => dispatch(push(ROUTES.BASE_PATH)), [dispatch]);
  const goBack = useCallback(() => dispatch(goBackReactRouter()), [dispatch]);

  const detail = useRescheduleSessionDetail(params.id);
  const { photoSessionDetail } = detail;
  const propertyId = photoSessionDetail && photoSessionDetail.house && photoSessionDetail.house.id;
  const isLastPhotographerReschedule = Boolean(
    photoSessionDetail && photoSessionDetail.lastPhotographerReschedule,
  );
  const photographerReschedulesCount =
    photoSessionDetail && photoSessionDetail.photographerReschedulesCount;

  // Only the logged photographer's agenda matters while keeping the session; otherwise the session may go
  // to any available photographer (the user opted out, or the reschedule cap forces it), so the list must
  // show the region-wide availability instead. Mirrors the requireLastAgent sent on submit below.
  const shouldFilterByCurrentAgent = keepSessionEnabled && !isLastPhotographerReschedule && keepSession;
  // Wait for the keep-session flag so we don't fetch with the wrong filter and then discard it.
  let schedulesDatesAgentFilter;
  if (!isKeepSessionFlagLoading) {
    schedulesDatesAgentFilter = shouldFilterByCurrentAgent ? userId : null;
  }
  const schedulesDates = useSchedulesDates(propertyId, schedulesDatesAgentFilter, {
    onError: (error) => logUncaught(error, { context: 'fetchSchedulesDates', propertyId }),
  });

  const possibleDatesAndTime = useMemo(
    () => mapSchedulesDatesAndTime(schedulesDates.data && schedulesDates.data.schedules, intl),
    [schedulesDates.data, intl],
  );
  // Kept by date value: a refetch can add or drop dates.
  const selectedDay = possibleDatesAndTime.find(({ value }) => value === chosenDate) || possibleDatesAndTime[0];
  const selectedTime = selectedDay && selectedDay.times.some(({ name }) => name === chosenTime)
    ? chosenTime
    : undefined;

  const onChangeChosenDate = ({ target: { value } }) => {
    setChosenDate(value);
    setChosenTime();
  };

  const onChangeChosenTime = ({ target: { value } }) => setChosenTime(Number(value));

  const handleOnClickContinue = async () => {
    dispatch(photoSessionRescheduleConfirmOnClickAnalytics({
      photoSessionId: params.id,
      newSessionType: SESSION_TYPE.FIXED,
    }));
    setIsSubmited(true);
    try {
      const fixedPayload = buildFixedPayload({
        photoSessionDetail,
        date: selectedDay.value,
        beginSlot: selectedTime,
        requireLastAgent: keepSessionEnabled && !isLastPhotographerReschedule && keepSession,
      });
      const { photographerId } = await rescheduleFixedSession({ propertyId, body: fixedPayload });
      setNewSessionAgentId(photographerId);
      dispatch(photoSessionRescheduleSuccessViewedAnalytics({
        photoSessionId: params.id,
        samePhotographerResult: photographerId === userAgentId,
      }));
    } catch (error) {
      if (isLastPhotographerUnavailableError(error)) {
        setRescheduleError(RESCHEDULE_ERROR.LAST_PHOTOGRAPHER_UNAVAILABLE);
        setChosenDate();
        setChosenTime();
        schedulesDates.refetch();
      } else {
        logUncaught(error, { context: 'rescheduleFixedSession', propertyId });
        setRescheduleError(RESCHEDULE_ERROR.GENERIC);
      }
    } finally {
      setIsSubmited(false);
    }
  };

  const onCloseSnackBarError = () => setRescheduleError();

  const actionButtons = buildButtons({
    handleOnClickContinue,
    handleOnClickBack: goBack,
    disableContinueBtn: selectedTime === undefined,
    continueButtonLabel: messages.scheduleButtonLabel,
  });

  const renderBody = () => {
    if (detail.isError) {
      return <ErrorState title={globalMessages.sessionLoadError} onRetry={detail.refetch} />;
    }

    // First load with no data, or a failed filter switch still holding keepPreviousData from the
    // previous agent filter — don't show those slots as if they belonged to the new filter.
    // A failed refetch after a 409 (same key, isPreviousData false) keeps the loaded list.
    if (schedulesDates.isError && (!schedulesDates.data || schedulesDates.isPreviousData)) {
      return <ErrorState title={messages.schedulesDatesErrorTitle} onRetry={schedulesDates.refetch} />;
    }

    // isLoading is only true for the very first fetch; later ones (e.g. toggling keep-session) keep the
    // previous list visible via keepPreviousData, so only that section needs to show as reloading.
    if (detail.isLoading || schedulesDates.isLoading || isKeepSessionFlagLoading) {
      return <LoadingPage />;
    }

    // True for any in-flight request — keep-session toggle and the refetch() after a 409 — so chips
    // swap to spinners on both paths while the rest of the form stays mounted.
    const isReloadingSchedules = schedulesDates.isFetching;

    // keepSession starts checked, so an empty personal agenda is the case the checkbox exists for.
    // Leave it on this screen so the photographer can uncheck it and load region-wide availability.
    if (!possibleDatesAndTime.length && !isReloadingSchedules) {
      return (
        <EmptySchedulesState
          onBack={goBack}
          showKeepSessionOption={keepSessionEnabled}
          keepSession={keepSession}
          onChangeKeepSession={onChangeKeepSession}
        />
      );
    }

    return (
      <SchedulesDatesContent
        // `selectedDay` can be undefined while reloading an empty list (e.g. toggling keep-session
        // right after a previous fetch returned no slots at all).
        chosenDate={selectedDay && selectedDay.value}
        chosenTime={selectedTime}
        isSubmited={isSubmited}
        isSameAgent={newSessionAgentId === userAgentId}
        isSessionRescheduled={!!newSessionAgentId}
        isReloadingSchedules={isReloadingSchedules}
        rescheduleError={rescheduleError}
        actionButtons={actionButtons}
        onChangeChoosenDate={onChangeChosenDate}
        onChangeChoosenTime={onChangeChosenTime}
        onCloseSnackBarError={onCloseSnackBarError}
        possibleDatesAndTime={possibleDatesAndTime}
        showKeepSessionOption={keepSessionEnabled && !isLastPhotographerReschedule}
        keepSession={keepSession}
        onChangeKeepSession={onChangeKeepSession}
        isLastPhotographerReschedule={isLastPhotographerReschedule}
        photographerReschedulesCount={photographerReschedulesCount}
        goToBasePath={goToBasePath}
      />
    );
  };

  return (
    <Fragment>
      <AppBar onBack={goBack} title={messages.headerTitle} />
      {renderBody()}
    </Fragment>
  );
};

SchedulesDates.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
};

export default SchedulesDates;
